import classNames from 'classnames';
import { Autoplay, EffectFade, Thumbs } from 'swiper/modules';
import { Swiper, SwiperClass, SwiperSlide } from 'swiper/react';
import { SwiperOptions } from 'swiper/types';
import Image from '@/components/atoms/image/image';
import Typography from '@/components/atoms/typography/typography';
import { HeaderCarouselItemProps } from '../header-carousel-item/header-carousel-item';
import styles from './header-carousel-preview.module.scss';

export interface HeaderCarouselPreviewProps {
  /** The class name to apply */
  className?: string;
  /** The configuration for the swiper instance */
  configuration: SwiperOptions;
  /** Current slide index */
  currentIndex?: number;
  /** Indicates whether autoplay is active */
  isPlaying?: boolean;
  /** The items to show as preview slides */
  items: HeaderCarouselItemProps[];
  /** The event triggered when initiating the swiper instance */
  onSwiper: (swiper: SwiperClass) => void;
  /** Timer duration in ms */
  timerDuration?: number;
}

const HeaderCarouselPreview = ({
  className,
  configuration,
  currentIndex = 0,
  isPlaying = false,
  items = [],
  onSwiper,
  timerDuration = 5000,
}: HeaderCarouselPreviewProps) => {
  return (
    <div className={classNames(styles.component, className)}>
      <Swiper
        {...configuration}
        className={styles.swiper}
        effect="fade"
        fadeEffect={{
          crossFade: true,
        }}
        modules={[Autoplay, EffectFade, Thumbs]}
        onSwiper={onSwiper}
        watchSlidesProgress
      >
        {items.map((item, itemIndex) => {
          const backgroundImage = item.mediaItem
            ? 'image' in item.mediaItem
              ? item.mediaItem.image
              : 'video' in item.mediaItem
                ? item.mediaItem.video.placeholderImage
                : undefined
            : undefined;

          return (
            <SwiperSlide key={itemIndex} className={styles.item}>
              {item.headline && (
                <Typography className={styles.headline} tagStyle="labelMedium">
                  {item.headline}
                </Typography>
              )}

              <div className={styles.imageWrapper}>
                {backgroundImage && (
                  <Image
                    alt={backgroundImage.altText}
                    aspectRatio="sixteenByNine"
                    className={styles.image}
                    focalPoint={backgroundImage.focalPoint}
                    height={86}
                    src={backgroundImage.url}
                    unoptimized
                    width={152}
                  />
                )}

                <div
                  key={`${itemIndex}-${currentIndex}`}
                  className={classNames(styles.progress, {
                    [styles.isActive]: itemIndex === currentIndex,
                    [styles.isPaused]: !isPlaying,
                  })}
                  style={{ animationDuration: `${timerDuration}ms` }}
                />
              </div>
            </SwiperSlide>
          );
        })}
      </Swiper>
    </div>
  );
};

HeaderCarouselPreview.displayName = 'HeaderCarouselPreview';

export default HeaderCarouselPreview;
